import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, Typography, FormControl, Select, MenuItem, IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails, Avatar, Menu } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import dayjs from 'dayjs';
import { getAllAgents, getAllAgentGroups } from '../../api/userApi';
import { useUser } from '../../context/userContext';
import { getAllSales } from '../../api/salesApi';
import { getAllActiveBuddies } from '../../api/userBuddyApi';
import { getManagerAssignments } from '../../api/managerAssignmentApi';
import { getTiers, getCurrentTierBulk, getTierStatusBulk } from '../../api/tierApi';
import axiosInstance from '../../api/axiosInstance';
import { handlePriceWithRM } from '../../utils/priceUtils';

const TIER_BADGE = {
  slate:   { bg: '#F1F5F9', fg: '#475569' },
  bronze:  { bg: '#FEF3C7', fg: '#92400E' },
  silver:  { bg: '#E5E7EB', fg: '#374151' },
  gold:    { bg: '#FEF3C7', fg: '#B45309' },
  diamond: { bg: '#DBEAFE', fg: '#1D4ED8' },
};

// Compact money for the card's Sales Ranking bar — matches the profile card's
// fmtRm (RM 240k / RM 1.2mil) so the two surfaces read identically.
function fmtRmShort(v) {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `RM ${(Math.round(n / 100_000) / 10).toString().replace(/\.0$/, '')}mil`;
  if (n >= 1_000)     return `RM ${Math.round(n / 1_000)}k`;
  return `RM ${Math.round(n).toLocaleString()}`;
}

function getInitials(firstName, lastName, alias) {
  if (firstName && lastName) return (lastName[0] + firstName[0]).toUpperCase();
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  if (alias) return alias.slice(0, 2).toUpperCase();
  return '??';
}

// Build tree from flat user list. Walks UserBuddy where buddy rows exist
// (multi-buddy agents appear as children of each upline with their split %),
// falls back to user.uplineId for users without buddy rows. Each tree node
// gets a unique nodeKey so React rendering of duplicated cards stays stable.
function buildTree(users, buddiesByUser, managerIdByAgent = {}) {
  // Build per-user buddy lists from the global map, but only keep entries
  // where the upline is also in the visible users list (groups filter etc.).
  const visibleIds = new Set(users.map(u => u.userId));

  // children[parentId] = [{ user, splitPercent, otherBuddies, nodeKey, viaManager }]
  const children = {};
  const placedAsChild = new Set();

  users.forEach(u => {
    // Group Admins are NOT commission-earners and shouldn't be drawn as
    // downlines under the agents they administer. Skip the place-as-child
    // logic so they appear as their own root, sitting alongside the
    // group's leader in the rendered tree row.
    const isGroupAdmin = (u.userRoles || []).some(r => r.roleName === 'Group Admin');
    if (isGroupAdmin) {
      return;
    }
    // All buddies regardless of visibility — needed so the "Also buddied to"
    // label can still surface uplines that happen to be outside the current
    // viewer's visible set.
    const allBuddies = buddiesByUser[u.userId] || [];
    const visibleBuddies = allBuddies.filter(b => visibleIds.has(b.uplineId));
    if (visibleBuddies.length > 0) {
      visibleBuddies.forEach(b => {
        if (!children[b.uplineId]) children[b.uplineId] = [];
        children[b.uplineId].push({
          user: u,
          splitPercent: Number(b.splitPercent),
          // Surface every OTHER buddy upline (visible or not) so the card
          // label clearly shows the multi-buddy relationships.
          otherBuddies: allBuddies.filter(x => x.uplineId !== b.uplineId),
          nodeKey: `${u.userId}-${b.uplineId}`,
          viaManager: false,
        });
        placedAsChild.add(u.userId);
      });
    } else if (u.uplineId && visibleIds.has(u.uplineId)) {
      // Fallback: legacy single-upline link.
      if (!children[u.uplineId]) children[u.uplineId] = [];
      children[u.uplineId].push({
        user: u,
        splitPercent: 100,
        otherBuddies: allBuddies.filter(x => x.uplineId !== u.uplineId),
        nodeKey: `${u.userId}-${u.uplineId}`,
        viaManager: false,
      });
      placedAsChild.add(u.userId);
    }

    // ALSO place under the manager (if any) so the MOC payer's branch
    // shows the managed agent as a child. The manager-branch placement
    // is rendered with a "MGR" tag so it's clearly distinct from the
    // buddy/upline placement.
    const managerId = managerIdByAgent[u.userId];
    if (managerId && managerId !== u.userId && visibleIds.has(managerId)) {
      if (!children[managerId]) children[managerId] = [];
      children[managerId].push({
        user: u,
        splitPercent: 100,
        otherBuddies: [],
        nodeKey: `${u.userId}-mgr-${managerId}`,
        viaManager: true,
      });
      placedAsChild.add(u.userId);
    }
  });

  // Recursive shape builder. Cycles can't form because buddy effective dates
  // and admin UI prevent self-buddy, but cap depth defensively.
  function build(userId, depth) {
    const u = users.find(x => x.userId === userId);
    if (!u || depth > 25) return null;
    const node = { ...u, children: [] };
    (children[userId] || []).forEach(c => {
      const childNode = build(c.user.userId, depth + 1);
      if (childNode) {
        childNode.splitPercent = c.splitPercent;
        childNode.otherBuddies = c.otherBuddies;
        childNode.nodeKey = c.nodeKey;
        childNode.viaManager = c.viaManager;
        node.children.push(childNode);
      }
    });
    return node;
  }

  const roots = users
    .filter(u => !placedAsChild.has(u.userId))
    .map(u => {
      const node = build(u.userId, 0);
      if (node) {
        node.splitPercent = 100;
        node.otherBuddies = [];
        node.nodeKey = `root-${u.userId}`;
      }
      return node;
    })
    .filter(Boolean);

  return roots;
}

// Recursive agent node component
function AgentNode({ agent, salesByAgent, level = 0, expandedNodes, toggleNode, managerNameByAgent = {}, managerIdByAgent = {}, canSeeAll = false, viewerId = 0, viewerGroupName = '', isViewerLeader = false, downlineSetForViewer = null, uplineSetForViewer = null, tierByUser = {}, tierStatusByUser = {}, tiers = [], canEditTier = false, onOverrideTier }) {
  const hasChildren = agent.children && agent.children.length > 0;
  const isExpanded = expandedNodes[agent.userId] !== false; // default expanded
  const isRootNode = level === 0;

  // Pill visibility per viewer's role:
  //   Super Admin / Accountant / Leader → see all 4 pills (PSC/DOC/TOC/MOC).
  //   Everyone else (regular upline / manager) → only DOC + MOC pills,
  //   because those are the rates that actually flow up to them. PSC and
  //   TOC are private to the agent + their group leader respectively.
  // Pill visibility — only show rates that flow to the viewer.
  //   PSC : own card; OR viewer is leader of agent's group (Leader exception)
  //   DOC : agent is in viewer's downline tree; OR Leader-of-group
  //   TOC : viewer is leader of agent's group
  //   MOC : viewer is the assigned manager of this agent
  // Full access (Super Admin / Accountant) overrides everything.
  // Leader exception: a Leader sees PSC + DOC + TOC of EVERY member of
  // their own group — that's the team rates flowing through them.
  const isOwnCard = agent.userId === viewerId;
  const isInViewerDownline = !!downlineSetForViewer && downlineSetForViewer.has(agent.userId);
  const isUplineOnly = !!uplineSetForViewer && uplineSetForViewer.has(agent.userId);
  const isAgentInViewerGroup = !!viewerGroupName && agent.groupName === viewerGroupName;
  const isLeaderOfThisGroup = isViewerLeader && isAgentInViewerGroup;
  const isViewerTheManager = managerIdByAgent[agent.userId] === viewerId;
  // Non-earner users (Admin / Astern Admin / Group Admin / Viewer without
  // any commission-earning role per CLAUDE.md rule 2) don't earn commission
  // or generate sales. Hide pills and totals so the card is identity-only.
  // Variable kept as ADMIN_ROLES for git-history continuity.
  const ADMIN_ROLES = ['Group Admin', 'Admin', 'Astern Admin', 'Viewer'];
  const EARNER_ROLES = ['Sales Agent', 'In-house Agent', 'Leader', 'Referral'];
  const agentRoleNames = (agent.userRoles || []).map(r => r.roleName);
  const isPureAdmin = agentRoleNames.some(r => ADMIN_ROLES.includes(r))
    && !agentRoleNames.some(r => EARNER_ROLES.includes(r));
  const hideCommission = isUplineOnly || isPureAdmin;
  // Upline cards render identity only — none of these rates flow to the
  // viewer, so showing them would be misleading. Same for pure-admin cards.
  const showPsc = !hideCommission && (canSeeAll || isOwnCard || isLeaderOfThisGroup);
  const showDoc = !hideCommission && (canSeeAll || isInViewerDownline || isLeaderOfThisGroup);
  const showToc = !hideCommission && (canSeeAll || isLeaderOfThisGroup);
  const showMoc = !hideCommission && (canSeeAll || isViewerTheManager);
  const showSalesTotals = !hideCommission;

  const initials = getInitials(agent.firstName, agent.lastName, agent.alias);
  const name = `${agent.lastName ?? ''} ${agent.firstName ?? ''}`.trim() || agent.alias || '';
  const roleName = agent.userRoles?.[0]?.roleName ?? 'Agent';
  const totalSales = salesByAgent[agent.userId]?.total ?? 0;
  const monthlySales = salesByAgent[agent.userId]?.monthly ?? 0;

  // Tier badge — only Sales Agents are tier-eligible. Click chip → Super
  // Admin override menu. Non-admins see chip read-only.
  const tier = tierByUser[agent.userId] || null;
  const isSalesAgent = (agent.userRoles ?? []).some(r => r.roleName === 'Sales Agent');
  const [tierMenuAnchor, setTierMenuAnchor] = useState(null);
  const tierPalette = TIER_BADGE[tier?.badgeColor] ?? TIER_BADGE.slate;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Connector line from parent */}
      {level > 0 && <div style={{ width: 2, height: 16, background: '#E2E8F0' }} />}

      {/* Agent Card */}
      <div style={{
        background: 'white',
        border: isRootNode ? '2px solid #1A8CFF' : hasChildren ? '1.5px solid #8B5CF6' : '1px solid #E2E8F0',
        borderRadius: 12,
        padding: '12px 14px',
        width: isRootNode ? 260 : 210, maxWidth: 'calc(100vw - 48px)',
        boxShadow: isRootNode ? '0 2px 8px rgba(26,140,255,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
        fontFamily: "'Poppins', sans-serif",
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{
            width: isRootNode ? 36 : 28, height: isRootNode ? 36 : 28, borderRadius: '50%',
            background: isRootNode ? 'linear-gradient(135deg,#1A8CFF,#6366F1)' : hasChildren ? '#EDE9FE' : '#E0E7FF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: isRootNode ? 'white' : hasChildren ? '#7C3AED' : '#3730A3',
            fontWeight: 700, fontSize: isRootNode ? 13 : 10, flexShrink: 0,
          }}>
            {initials}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isRootNode ? 13 : 11, fontWeight: 600, color: '#0F172A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              {/* Buddy split badge — only shown when this agent has multiple buddies */}
              {agent.otherBuddies && agent.otherBuddies.length > 0 && (
                <>
                  <span style={{ fontSize: 9, fontWeight: 700, color: '#7C3AED', background: '#EDE9FE', padding: '1px 4px', borderRadius: 3 }}>
                    {Number(agent.splitPercent || 100).toFixed(0)}%
                  </span>
                  <Tooltip
                    title={`Also buddied to: ${agent.otherBuddies.map(b => b.uplineName).filter(Boolean).join(', ') || '—'}`}
                  >
                    <LinkIcon sx={{ fontSize: 12, color: '#7C3AED' }} />
                  </Tooltip>
                </>
              )}
            </div>
            <div style={{
              fontSize: 9, fontWeight: 600, textTransform: 'uppercase',
              color: isRootNode ? '#1A8CFF' : hasChildren ? '#7C3AED' : '#3730A3',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {roleName}
            </div>
            {isSalesAgent && (
              <div style={{ marginTop: 2 }}>
                <span
                  onClick={canEditTier ? (e) => { e.stopPropagation(); setTierMenuAnchor(e.currentTarget); } : undefined}
                  style={{
                    display: 'inline-block',
                    fontSize: 9, fontWeight: 700,
                    background: tierPalette.bg, color: tierPalette.fg,
                    padding: '1px 6px', borderRadius: 6,
                    cursor: canEditTier ? 'pointer' : 'default',
                    border: canEditTier ? '1px dashed transparent' : 'none',
                  }}
                  onMouseEnter={(e) => { if (canEditTier) e.currentTarget.style.borderColor = tierPalette.fg; }}
                  onMouseLeave={(e) => { if (canEditTier) e.currentTarget.style.borderColor = 'transparent'; }}
                  title={canEditTier ? 'Click to override tier (Super Admin)' : ''}
                >
                  {tier?.name ?? 'No Tier'}
                </span>
                <Menu
                  anchorEl={tierMenuAnchor}
                  open={Boolean(tierMenuAnchor)}
                  onClose={() => setTierMenuAnchor(null)}
                >
                  <Typography sx={{ px: 2, py: 1, fontSize: '0.7rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
                    Override tier (skips approval)
                  </Typography>
                  {tiers.map(t => (
                    <MenuItem
                      key={t.tierId}
                      selected={tier?.tierId === t.tierId}
                      onClick={() => { setTierMenuAnchor(null); onOverrideTier?.(agent.userId, t); }}
                      sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' }}
                    >
                      {t.name} — {t.pscPercent}% (≥ RM {Number(t.revenueThreshold).toLocaleString()})
                    </MenuItem>
                  ))}
                </Menu>
              </div>
            )}
            {hasChildren && (
              <div style={{
                fontSize: 9, fontWeight: 400, textTransform: 'uppercase',
                color: '#94A3B8', whiteSpace: 'nowrap',
                marginTop: 1,
              }}>
                {agent.children.length} DOWNLINE{agent.children.length > 1 ? 'S' : ''}
              </div>
            )}
            {agent.viaManager && (
              <div style={{
                display: 'inline-block', marginTop: 2,
                fontSize: 8, fontWeight: 700, color: '#5B21B6',
                background: '#EDE9FE', padding: '1px 5px', borderRadius: 4,
                letterSpacing: '0.5px',
              }}>
                MANAGED AGENT
              </div>
            )}
            {managerNameByAgent[agent.userId] && (
              <div style={{
                fontSize: 9, fontWeight: 500,
                color: '#5B21B6',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                marginTop: 1,
              }}>
                <span style={{ fontWeight: 700, color: '#5B21B6' }}>Manager:</span>{' '}
                <span style={{ color: '#0F172A' }}>{managerNameByAgent[agent.userId]}</span>
              </div>
            )}
            {agent.otherBuddies && agent.otherBuddies.length > 0 && (
              <div style={{
                fontSize: 9, fontWeight: 500,
                color: '#7C3AED',
                marginTop: 1,
                wordBreak: 'break-word',
              }}>
                <span style={{ fontWeight: 700 }}>Also buddied to:</span>{' '}
                <span style={{ color: '#0F172A' }}>
                  {agent.otherBuddies.map(b => b.uplineName).filter(Boolean).join(', ')}
                </span>
              </div>
            )}
          </div>
          {hasChildren && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleNode(agent.userId); }}
              sx={{ padding: '2px', color: '#94A3B8' }}>
              {isExpanded ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          )}
        </div>

        {/* Commission badges — 4 equal-width pills (PSC / DOC / TOC / MOC).
            MOC pill always renders so widths line up between cards; the
            value is dim/zero when the agent has no manager assigned. */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 6 }}>
          {showPsc && (
            <div style={{ background: '#E8F4FF', borderRadius: 4, padding: '3px 4px', textAlign: 'center', flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 7, color: '#64748B', fontWeight: 600 }}>PSC</div>
              <div style={{ fontSize: isRootNode ? 12 : 10, color: '#0F172A', fontWeight: 700 }}>{agent.personalComm ?? 0}%</div>
            </div>
          )}
          {showDoc && (
            <div style={{ background: '#FFF7ED', borderRadius: 4, padding: '3px 4px', textAlign: 'center', flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 7, color: '#92400E', fontWeight: 600 }}>DOC ↑</div>
              <div style={{ fontSize: 10, color: '#92400E', fontWeight: 700 }}>{agent.downlineOverridingComm ?? 0}%</div>
            </div>
          )}
          {showToc && (
            <div style={{ background: '#F0FDF4', borderRadius: 4, padding: '3px 4px', textAlign: 'center', flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 7, color: '#065F46', fontWeight: 600 }}>TOC ↑</div>
              <div style={{ fontSize: 10, color: '#065F46', fontWeight: 700 }}>{agent.teamOverridingComm ?? 0}%</div>
            </div>
          )}
          {showMoc && (() => {
            const moc = agent.managerOverridingComm ?? 0;
            const dim = !moc;
            return (
              <div style={{ background: dim ? '#F8FAFC' : '#F5F3FF', borderRadius: 4, padding: '3px 4px', textAlign: 'center', flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 7, color: dim ? '#94A3B8' : '#5B21B6', fontWeight: 600 }}>MOC ↑</div>
                <div style={{ fontSize: 10, color: dim ? '#94A3B8' : '#5B21B6', fontWeight: 700 }}>{moc}%</div>
              </div>
            );
          })()}
        </div>

        {/* Sales — hidden on upline-only cards (private to the agent/leader) */}
        {showSalesTotals && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, paddingTop: 4, borderTop: '1px solid #F1F5F9' }}>
            <span style={{ color: '#64748B' }}>Total: <strong style={{ color: '#0F172A' }}>{handlePriceWithRM(totalSales)}</strong></span>
            <span style={{ color: '#64748B' }}>Mo: <strong style={{ color: '#10B981' }}>{handlePriceWithRM(monthlySales)}</strong></span>
          </div>
        )}

        {/* Sales Ranking progress — the same bar as the profile's Sales Ranking card,
            fed by Tier/StatusBulk so the numbers can't drift. Behind the same
            showSalesTotals gate as the totals above (revenue is private on
            upline-only cards), and hidden for tier-ineligible users (In-house
            Agents / Leaders / admins) exactly like the profile card. */}
        {showSalesTotals && (() => {
          const st = tierStatusByUser[agent.userId];
          if (!st || st.isEligible === false) return null;
          if (!st.currentTier && !st.nextTier) return null;
          const atTop = !st.nextTier;
          const pct = atTop ? 100 : Math.min(100, Math.max(0, Number(st.progressPct) || 0));
          const barColor = atTop ? '#10B981' : '#1A8CFF';
          const namePalette = TIER_BADGE[st.currentTier?.badgeColor] ?? TIER_BADGE.slate;
          return (
            <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid #F1F5F9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <span style={{ fontSize: 8, fontWeight: 700, color: namePalette.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {st.currentTier?.name ?? 'Unranked'}
                </span>
                <span style={{ fontSize: 8, fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>
                  {fmtRmShort(st.accumulatedRevenue)}
                </span>
              </div>
              <div style={{ height: 4, borderRadius: 2, background: '#E2E8F0', overflow: 'hidden' }}>
                <div style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: barColor }} />
              </div>
              <div style={{ fontSize: 7, color: atTop ? '#10B981' : '#64748B', fontWeight: atTop ? 700 : 500, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {atTop
                  ? 'Top tier reached'
                  : `${fmtRmShort(st.revenueToNextTier)} to ${st.nextTier.name}`}
              </div>
            </div>
          );
        })()}
        {isUplineOnly && (
          <div style={{ marginTop: 6, paddingTop: 4, borderTop: '1px solid #F1F5F9', textAlign: 'center' }}>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Your upline · view only
            </span>
          </div>
        )}
      </div>

      {/* Children / Downlines */}
      {hasChildren && isExpanded && (
        <>
          <div style={{ width: 2, height: 12, background: '#E2E8F0' }} />
          {agent.children.length > 1 && (
            <div style={{ width: Math.min(agent.children.length * 226, 900), height: 2, background: '#E2E8F0' }} />
          )}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center', width: '100%' }}>
            {agent.children.map(child => (
              <AgentNode
                key={child.nodeKey ?? child.userId}
                agent={child}
                salesByAgent={salesByAgent}
                level={level + 1}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                managerNameByAgent={managerNameByAgent}
                managerIdByAgent={managerIdByAgent}
                canSeeAll={canSeeAll}
                viewerId={viewerId}
                viewerGroupName={viewerGroupName}
                isViewerLeader={isViewerLeader}
                downlineSetForViewer={downlineSetForViewer}
                uplineSetForViewer={uplineSetForViewer}
                tierByUser={tierByUser}
                tierStatusByUser={tierStatusByUser}
                tiers={tiers}
                canEditTier={canEditTier}
                onOverrideTier={onOverrideTier}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Find all downline IDs recursively for a given user. Walks buddies
// (multi-upline aware), falls back to uplineId for users without rows.
function getDownlineIds(userId, allUsers, buddiesByUser) {
  const ids = new Set();
  const queue = [userId];
  while (queue.length > 0) {
    const current = queue.shift();
    allUsers.forEach(u => {
      if (ids.has(u.userId) || u.userId === current) return;
      const myBuddies = buddiesByUser?.[u.userId] || [];
      const linksToCurrent = myBuddies.length > 0
        ? myBuddies.some(b => b.uplineId === current)
        : u.uplineId === current;
      if (linksToCurrent) {
        ids.add(u.userId);
        queue.push(u.userId);
      }
    });
  }
  return ids;
}

// Find upline chain for a user. With buddies, "chain" expands into a set
// (every buddy + their buddies, recursively).
function getUplineChain(userId, allUsers, buddiesByUser) {
  const chain = new Set();
  const queue = [userId];
  const visited = new Set([userId]);
  while (queue.length > 0) {
    const cur = queue.shift();
    const u = allUsers.find(x => x.userId === cur);
    if (!u) continue;
    const myBuddies = buddiesByUser?.[cur] || [];
    const uplines = myBuddies.length > 0
      ? myBuddies.map(b => b.uplineId)
      : (u.uplineId ? [u.uplineId] : []);
    uplines.forEach(uid => {
      if (visited.has(uid)) return;
      visited.add(uid);
      chain.add(uid);
      queue.push(uid);
    });
  }
  return [...chain];
}

export default function AgentHierarchy() {
  const { user } = useUser();
  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [sales, setSales] = useState([]);
  const [buddies, setBuddies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [groupFilter, setGroupFilter] = useState('All');
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs());
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});

  const [managerAssignments, setManagerAssignments] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [tierByUser, setTierByUser] = useState({});
  const [tierStatusByUser, setTierStatusByUser] = useState({});
  const isSuperAdmin = !!user?.roles?.includes('Super Admin');

  const fetchData = useCallback(async () => {
    try {
      const [usersRes, groupsRes, salesRes, buddiesRes, mgrRes, tiersRes] = await Promise.all([
        getAllAgents(),
        getAllAgentGroups(),
        getAllSales(),
        getAllActiveBuddies().catch(() => []),
        getManagerAssignments().catch(() => []),
        getTiers().catch(() => ({ data: [] })),
      ]);
      const activeUsers = usersRes.data.filter(u => u.status === 'Active');
      setUsers(activeUsers);
      setGroups(groupsRes.data);
      setSales(salesRes.data);
      setBuddies(buddiesRes || []);
      setManagerAssignments(mgrRes || []);
      setTiers(tiersRes.data || []);

      const ids = activeUsers.map(u => u.userId);
      if (ids.length > 0) {
        getCurrentTierBulk(ids).then(r => setTierByUser(r.data || {})).catch(() => {});
        // Sales Ranking progress per card — one bulk call, same numbers as the
        // profile's Sales Ranking card (Tier/StatusBulk).
        getTierStatusBulk(ids).then(r => setTierStatusByUser(r.data || {})).catch(() => {});
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOverrideTier = useCallback(async (userId, tier) => {
    const u = users.find(x => x.userId === userId);
    const userName = u ? `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.alias : `User ${userId}`;
    if (!window.confirm(`Override ${userName}'s tier to "${tier.name}" (${tier.pscPercent}% PSC)?\n\nThis bypasses the approval flow. A new commission-history row is written with EffectiveFrom = today.`)) return;
    try {
      await axiosInstance.post(`/Tier/OverrideTier?userId=${userId}&tierId=${tier.tierId}`);
      // Optimistically update so the badge changes immediately.
      setTierByUser(prev => ({ ...prev, [userId]: tier }));
    } catch (e) {
      alert(`Failed: ${e.response?.data ?? e.message}`);
    }
  }, [users]);

  // Group buddy rows by UserId for buildTree() and downline-walk helpers.
  const buddiesByUser = useMemo(() => {
    const map = {};
    (buddies || []).forEach(b => {
      if (!map[b.userId]) map[b.userId] = [];
      map[b.userId].push(b);
    });
    return map;
  }, [buddies]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Calculate sales by agent for the date range
  const salesByAgent = useMemo(() => {
    const map = {};
    const monthStart = startDate.startOf('month');
    const monthEnd = startDate.endOf('month');

    sales.forEach(s => {
      if (!s.depositOn) return;
      const date = dayjs(s.depositOn);
      const inRange = date.isAfter(startDate.subtract(1, 'day')) && date.isBefore(endDate.add(1, 'day'));
      const inMonth = date.isAfter(monthStart.subtract(1, 'day')) && date.isBefore(monthEnd.add(1, 'day'));
      const agentId = s.agentId;

      if (!map[agentId]) map[agentId] = { total: 0, monthly: 0 };
      if (inRange) map[agentId].total += (s.finalPrice ?? 0);
      if (inMonth) map[agentId].monthly += (s.finalPrice ?? 0);
    });
    return map;
  }, [sales, startDate, endDate]);

  // Visibility rules per memory feedback_hierarchy_visibility:
  //   Super Admin / Accountant -> see all
  //   Leader -> see whole group(s) they lead
  //   Manager (active ManagerAssignment.ManagerId == me) -> see ONLY the
  //   directly-managed agents. Manager visibility does NOT cascade into
  //   the managed agent's downlines (manager pays MOC on direct managed
  //   agents only; their downline trees are someone else's concern).
  //   Sales / In-house Agent -> see their own downlines (multi-level via
  //   UserBuddy + UplineId fallback). A buddy IS an upline, so a buddy
  //   sees their buddy-downline's full subtree.
  //   Admin / Astern Admin / Group Admin -> blocked at route level (no
  //   AgentHierarchy right). Don't add them here — these roles aren't
  //   commission earners and shouldn't browse the structure.
  const canSeeAll = user?.roles?.some(r =>
    ['Super Admin', 'Accountant'].includes(r));
  const isLeader = user?.roles?.includes('Leader');
  const myId = user?.userId;

  // Active manager assignments where I am the manager. Used to expand
  // visibility to managed agents (and their downlines).
  const managedAgentIds = useMemo(() => {
    if (!myId || !managerAssignments?.length) return [];
    return managerAssignments
      .filter(m => m.managerId === myId && !m.effectiveTo)
      .map(m => m.agentId);
  }, [myId, managerAssignments]);

  // Map agent userId -> manager full name, for the "Manager: <name>"
  // label rendered on each AgentNode card.
  const managerNameByAgent = useMemo(() => {
    if (!managerAssignments?.length || !users?.length) return {};
    const userById = {};
    users.forEach(u => { userById[u.userId] = u; });
    const result = {};
    managerAssignments
      .filter(m => !m.effectiveTo)
      .forEach(m => {
        const mgr = userById[m.managerId];
        if (!mgr) return;
        const name = `${mgr.lastName ?? ''} ${mgr.firstName ?? ''}`.trim() || mgr.alias || mgr.username || '—';
        result[m.agentId] = name;
      });
    return result;
  }, [managerAssignments, users]);

  // Map agent userId -> manager userId. Lets each agent card decide whether
  // to show the MOC pill (only the actual manager + admins/leaders see it).
  const managerIdByAgent = useMemo(() => {
    if (!managerAssignments?.length) return {};
    const result = {};
    managerAssignments
      .filter(m => !m.effectiveTo)
      .forEach(m => { result[m.agentId] = m.managerId; });
    return result;
  }, [managerAssignments]);

  // Set of userIds in the viewer's downline subtree (used by per-card pill
  // logic so DOC pill only shows on cards of agents whose DOC flows to me).
  const downlineSetForViewer = useMemo(() => {
    if (!myId) return new Set();
    const ids = getDownlineIds(myId, users, buddiesByUser);
    return new Set(ids);
  }, [myId, users, buddiesByUser]);

  // Set of userIds rendered as identity-only "view only" cards — just
  // the viewer's upline chain (no siblings, no cousins). Cards in this
  // set hide all commission pills and sales totals because none of
  // those values flow to the viewer.
  const uplineSetForViewer = useMemo(() => {
    if (canSeeAll || !myId) return new Set();
    return new Set(getUplineChain(myId, users, buddiesByUser));
  }, [canSeeAll, myId, users, buddiesByUser]);

  // Build hierarchies per group with role-based filtering
  const groupedHierarchy = useMemo(() => {
    let relevantUsers = users;

    if (!canSeeAll) {
      const visibleIds = new Set();

      // Visibility = UNION of every relationship that pays this viewer
      // commission. Per user rule: "as long as they have DOC/TOC contribute
      // up towards the person, they can view."
      //   Leader → whole group (they receive TOC)
      //   Manager → directly-managed agents only (they receive MOC). NOT
      //     the managed agent's family tree, NOT the manager themselves.
      //   Upline → own downline subtree (they receive DOC)
      // A pure-manager (only managerial role, no downlines / not a leader)
      // sees just the agents they manage — no self, no family tree.
      const ownDownlines = getDownlineIds(myId, users, buddiesByUser);
      ownDownlines.forEach(id => visibleIds.add(id));
      managedAgentIds.forEach(id => visibleIds.add(id));
      if (isLeader) {
        const myGroup = user?.groupName;
        users.forEach(u => { if (u.groupName === myGroup) visibleIds.add(u.userId); });
      }
      // Upline chain only — the viewer sees a straight line from each
      // of their uplines down to themselves. No siblings, no cousins,
      // no extended family tree.
      const uplineChain = getUplineChain(myId, users, buddiesByUser);
      uplineChain.forEach(id => visibleIds.add(id));
      // Always include self so the page shows at least the viewer's own
      // card (with their PSC), even when they have no downlines / managed
      // agents / group leadership.
      if (myId) visibleIds.add(myId);

      relevantUsers = users.filter(u => visibleIds.has(u.userId));
    }

    const filtered = groupFilter === 'All' ? relevantUsers : relevantUsers.filter(u => u.groupName === groupFilter);

    // Build the tree across ALL filtered users (not partitioned by group),
    // so a buddy relationship that crosses groups (e.g. CHUN WEI is in
    // "Don & Alice" but has buddy-uplines to TAN JIN SHENG in "A Team")
    // still places CHUN WEI under both roots. Then partition the resulting
    // ROOTS by their own groupName — each group section ends up showing
    // the trees whose root sits in that group, with subtrees that may
    // dip into other groups.
    const fullTree = buildTree(filtered, buddiesByUser, managerIdByAgent);

    const byGroup = {};
    // Count how many unique members each root's subtree contributes per
    // group (so a cross-group buddy descendant still counts in the host
    // group's "N agents" total).
    const countMembersByGroup = {};
    filtered.forEach(u => {
      const g = u.groupName ?? 'No Group';
      countMembersByGroup[g] = (countMembersByGroup[g] || 0) + 1;
    });
    fullTree.forEach(root => {
      const group = root.groupName ?? 'No Group';
      if (!byGroup[group]) byGroup[group] = [];
      byGroup[group].push(root);
    });

    return Object.entries(byGroup).map(([groupName, tree]) => ({
      groupName,
      tree,
      count: countMembersByGroup[groupName] || 0,
    })).sort((a, b) => a.groupName.localeCompare(b.groupName));
  }, [users, groupFilter, canSeeAll, isLeader, myId, user?.groupName, buddiesByUser, managedAgentIds, managerIdByAgent]);

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const toggleNode = (userId) => {
    setExpandedNodes(prev => ({ ...prev, [userId]: prev[userId] === false ? true : false }));
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
            Agent Hierarchy
          </h2>
          <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif" }}>
            Organization structure with performance
          </p>
        </div>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canSeeAll && (
            <FormControl size="small">
              <Select value={groupFilter} onChange={(e) => setGroupFilter(e.target.value)}
                sx={{ minWidth: 140, fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif", borderRadius: '8px', backgroundColor: 'white' }}>
                <MenuItem value="All">All Groups</MenuItem>
                {groups.map(g => (<MenuItem key={g.groupId} value={g.groupName}>{g.groupName}</MenuItem>))}
              </Select>
            </FormControl>
          )}
          <DatePicker value={startDate} onChange={(val) => val && setStartDate(val)} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } } } }} />
          <Typography sx={{ color: '#64748B', fontSize: '0.85rem' }}>to</Typography>
          <DatePicker value={endDate} onChange={(val) => val && setEndDate(val)} format="DD/MM/YYYY"
            slotProps={{ textField: { size: 'small', sx: { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } } } }} />
        </Box>
      </Box>

      {/* Groups */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {groupedHierarchy.map(({ groupName, tree, count }) => {
          const isExpanded = expandedGroups[groupName] !== false;

          return (
            <Card key={groupName} sx={{ overflow: 'visible' }}>
              {/* Group Header */}
              <Box onClick={() => toggleGroup(groupName)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, padding: '12px 16px',
                  cursor: 'pointer', background: '#F0F9FF', borderBottom: isExpanded ? '1px solid #E2E8F0' : 'none',
                  '&:hover': { background: '#E0F2FE' }, transition: 'background 0.15s',
                  borderRadius: isExpanded ? '14px 14px 0 0' : '14px',
                }}>
                <span style={{ color: '#1A8CFF', fontSize: 14, fontWeight: 700, fontFamily: "'Poppins', sans-serif" }}>
                  {isExpanded ? '▼' : '►'}
                </span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                  {groupName}
                </span>
                <span style={{ fontSize: 11, color: '#64748B', background: '#F1F5F9', padding: '2px 8px', borderRadius: 4, fontFamily: "'Poppins', sans-serif" }}>
                  {count} agents
                </span>
              </Box>

              {/* Expanded Tree */}
              {isExpanded && (
                <Box sx={{ padding: '20px 8px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'center', gap: 2, overflowX: 'auto', width: '100%' }}>
                  {tree.map(root => (
                    <AgentNode
                      key={root.nodeKey ?? root.userId}
                      agent={root}
                      salesByAgent={salesByAgent}
                      level={0}
                      expandedNodes={expandedNodes}
                      toggleNode={toggleNode}
                      managerNameByAgent={managerNameByAgent}
                      managerIdByAgent={managerIdByAgent}
                      canSeeAll={canSeeAll}
                      viewerId={myId}
                      viewerGroupName={user?.groupName ?? ''}
                      isViewerLeader={isLeader}
                      downlineSetForViewer={downlineSetForViewer}
                      uplineSetForViewer={uplineSetForViewer}
                      tierByUser={tierByUser}
                      tierStatusByUser={tierStatusByUser}
                      tiers={tiers}
                      canEditTier={isSuperAdmin}
                      onOverrideTier={handleOverrideTier}
                    />
                  ))}
                </Box>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
